import {useState} from 'react'
import {useQuery, useQueryClient} from 'react-query'
import {Modal} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {
  disableMfaMethod,
  getMfaMethods,
  getMfaStatus,
  setMfaEnforcement,
  MfaMethodItem,
  MfaMethodType,
  startMfaEnrollment,
  verifyMfaEnrollment,
} from '../../../../modules/auth/core/mfaApi'
import {MfaCodeInput} from '../../../../modules/auth/components/MfaCodeInput'
import {MfaMethodPicker} from '../../../../modules/auth/components/MfaMethodPicker'
import {useAuth} from '../../../../modules/auth'
import toaster from '../../../../../Utils/toaster'

type Props = {
  userId?: string
  mode: 'self' | 'admin'
}

const lastActiveMethodMessage =
  'Set up another verification method before you can turn this one off. You must always have at least one active method.'

export function MfaSettingsPanel({userId, mode}: Props) {
  const {logout, saveAuth} = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [setupMethod, setSetupMethod] = useState<MfaMethodType | null>(null)
  const [contact, setContact] = useState('')
  const [mfaSession, setMfaSession] = useState('')
  const [qrUri, setQrUri] = useState('')
  const [secretKey, setSecretKey] = useState('')
  const [code, setCode] = useState('')
  const [disableTarget, setDisableTarget] = useState<MfaMethodType | null>(null)
  const [totpScanned, setTotpScanned] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showMethodPicker, setShowMethodPicker] = useState(false)
  const [pickerSelection, setPickerSelection] = useState<MfaMethodType | null>(null)
  const [enforcementSaving, setEnforcementSaving] = useState(false)

  const {currentUser} = useAuth()
  const adminUserId = userId?.trim() || undefined
  const canManageEnforcement =
    mode === 'admin' || (mode === 'self' && currentUser?.result?.userType === 'SYSTEM')
  const statusUserId = mode === 'admin' ? adminUserId : undefined
  const {data: statusData, refetch: refetchStatus} = useQuery(
    ['mfa-status', statusUserId, mode],
    () => getMfaStatus(statusUserId),
    {enabled: canManageEnforcement && (mode !== 'admin' || !!adminUserId)}
  )
  const {data, refetch} = useQuery(
    ['mfa-methods', adminUserId, mode],
    () => getMfaMethods(mode === 'admin' ? adminUserId : undefined),
    {
      enabled: mode !== 'admin' || !!adminUserId,
      refetchOnWindowFocus: false,
    }
  )

  const methods = data?.data?.methods ?? []
  const activeCount = data?.data?.activeMethodCount ?? 0
  const status = statusData?.data
  const isMfaEnforced = status?.isMfaEnforced ?? true

  const handleEnforcementToggle = async (enabled: boolean) => {
    if (!canManageEnforcement) return
    if (mode === 'admin' && !adminUserId) {
      toaster('error', 'User profile is still loading. Please try again.')
      return
    }
    setEnforcementSaving(true)
    try {
      await setMfaEnforcement(enabled, mode === 'admin' ? adminUserId : undefined)
      await refetchStatus()
      queryClient.invalidateQueries(['mfa-status'])
      toaster('success', enabled ? 'MFA enabled for this user' : 'MFA disabled for this user')
    } catch {
      toaster('error', 'Failed to update MFA setting')
    } finally {
      setEnforcementSaving(false)
    }
  }

  const iconFor = (method: MfaMethodType) => {
    if (method === 2) return 'bi-phone'
    if (method === 0) return 'bi-chat-dots'
    return 'bi-envelope'
  }

  const setupCandidates = methods.filter((m) => m.canSetUp).map((m) => m.method)

  const openMethodPicker = (method: MfaMethodType) => {
    setPickerSelection(method)
    setShowMethodPicker(true)
  }

  const handleSetup = async (method: MfaMethodType) => {
    setSetupMethod(method)
    setCode('')
    setContact('')
    setTotpScanned(false)
    setMfaSession('')
    if (method === 2) {
      setLoading(true)
      try {
        const {data: res} = await startMfaEnrollment({method, targetUserId: mode === 'admin' ? adminUserId : undefined})
        setQrUri(res.qrCodeUri ?? '')
        setSecretKey(res.secretKey ?? '')
        setMfaSession(res.mfaSession ?? '')
      } catch {
        toaster('error', 'Failed to start setup')
        setSetupMethod(null)
      } finally {
        setLoading(false)
      }
    }
  }

  const sendContactCode = async () => {
    if (setupMethod == null || !contact.trim()) {
      toaster('error', setupMethod === 1 ? 'Enter an email address' : 'Enter a phone number')
      return
    }
    setLoading(true)
    try {
      const {data: res} = await startMfaEnrollment({
        method: setupMethod,
        contactValue: contact,
        mfaSession,
        targetUserId: mode === 'admin' ? adminUserId : undefined,
      })
      setMfaSession(res.mfaSession ?? mfaSession)
    } catch {
      toaster('error', 'Failed to send code')
    } finally {
      setLoading(false)
    }
  }

  const verifySetup = async () => {
    if (setupMethod == null) return
    setLoading(true)
    try {
      const {data: res} = await verifyMfaEnrollment({
        method: setupMethod,
        code,
        mfaSession,
        targetUserId: mode === 'admin' ? adminUserId : undefined,
      })
      if (!res.success) {
        toaster('error', 'Invalid code')
        return
      }
      setSetupMethod(null)
      refetch()
      queryClient.invalidateQueries(['mfa-status'])
      if (res.sessionInvalidated && res.token) {
        saveAuth({
          isAuthenticated: true,
          message: '',
          userName: res.userName,
          email: res.email,
          roles: res.roles ?? [],
          token: res.token,
          refreshTokenExpiration: res.refreshTokenExpiration,
        })
        toaster('success', 'Multi-factor authentication enabled')
      } else if (res.sessionInvalidated) {
        await logout()
        navigate('/auth/login')
      }
    } catch {
      toaster('error', 'Verification failed')
    } finally {
      setLoading(false)
    }
  }

  const requestDisable = (item: MfaMethodItem) => {
    if (mode === 'admin' && !adminUserId) {
      toaster('error', 'User profile is still loading. Please try again.')
      return
    }
    if (mode === 'self' && activeCount <= 1) {
      toaster('error', lastActiveMethodMessage)
      return
    }
    setDisableTarget(item.method)
  }

  const confirmDisable = async () => {
    if (disableTarget == null) return
    if (mode === 'admin' && !adminUserId) {
      toaster('error', 'User profile is still loading. Please try again.')
      return
    }
    setLoading(true)
    try {
      const {data: res} = await disableMfaMethod(disableTarget, mode === 'admin' ? adminUserId : undefined)
      setDisableTarget(null)
      if (res.sessionInvalidated && res.token) {
        saveAuth({
          isAuthenticated: true,
          message: '',
          userName: res.userName ?? '',
          email: res.email ?? '',
          roles: res.roles ?? [],
          token: res.token,
          refreshTokenExpiration: res.refreshTokenExpiration ?? '',
        })
      } else if (res.sessionInvalidated && mode === 'self') {
        logout()
        return
      }
      await refetch()
      queryClient.invalidateQueries(['mfa-status'])
      if (mode === 'admin') {
        toaster('success', 'MFA method removed. The user can set up again on next login.')
      }
    } catch (e: any) {
      const apiMessage = e?.response?.data?.message ?? e?.response?.data?.Message
      toaster(
        'error',
        apiMessage ?? (mode === 'admin' ? 'Failed to remove MFA method.' : lastActiveMethodMessage)
      )
      setDisableTarget(null)
    } finally {
      setLoading(false)
    }
  }

  const renderRow = (item: MfaMethodItem) => (
    <div key={item.method} className='d-flex align-items-center justify-content-between border rounded p-4 mb-3'>
      <div className='d-flex align-items-center gap-4'>
        <i className={`bi ${iconFor(item.method)} fs-2 text-gray-600`} />
        <div>
          <div className='fw-bold'>{item.methodName}</div>
          <div className={item.isEnabled ? 'text-success' : 'text-muted'}>{item.status}</div>
          <div className='text-muted small'>{item.subtext}</div>
        </div>
      </div>
      {item.canSetUp && (
        <button type='button' className='btn btn-sm btn-light-primary' onClick={() => openMethodPicker(item.method)}>
          Set up
        </button>
      )}
      {item.canToggle && item.isEnabled && mode === 'self' && (
        <div className='form-check form-switch'>
          <input
            className='form-check-input'
            type='checkbox'
            checked={item.isEnabled}
            onChange={() => requestDisable(item)}
          />
        </div>
      )}
      {mode === 'admin' && item.isEnabled && (
        <div className='form-check form-switch'>
          <input
            className='form-check-input'
            type='checkbox'
            checked={item.isEnabled}
            onChange={() => requestDisable(item)}
            disabled={loading || !adminUserId}
          />
        </div>
      )}
      {mode === 'self' && item.isEnabled && !item.canToggle && (
        <div
          className='form-check form-switch'
          style={{cursor: 'not-allowed'}}
          onClick={() => toaster('error', lastActiveMethodMessage)}
        >
          <input className='form-check-input' type='checkbox' checked disabled />
        </div>
      )}
      {mode === 'admin' && !item.isEnabled && (
        <div className='form-check form-switch'>
          <input className='form-check-input' type='checkbox' checked={false} disabled />
        </div>
      )}
    </div>
  )

  return (
    <div className='pt-4'>
      <h4 className='fw-bold mb-2'>Multi Factor Authentication</h4>
      {mode === 'self' && status?.showGracePeriodBanner && (
        <div className='alert alert-warning d-flex align-items-center gap-2'>
          <i className='bi bi-clock-history' />
          MFA setup is optional for the next <strong>{status.daysRemaining}</strong> days. After that, you will be required
          to complete setup the next time you sign in.
        </div>
      )}
      {canManageEnforcement && (
        <div className='d-flex align-items-center justify-content-between border rounded p-4 mb-4'>
          <div>
            <div className='fw-bold'>Enable/Disable MFA</div>
            <div className='text-muted small'>
              When disabled, this user will not be required to set up or use MFA at login.
            </div>
          </div>
          <div className='form-check form-switch m-0'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={isMfaEnforced}
              onChange={(e) => handleEnforcementToggle(e.target.checked)}
              disabled={enforcementSaving || (mode === 'admin' && !adminUserId)}
            />
          </div>
        </div>
      )}
      {mode === 'admin' && (
        <>
          <p className='text-muted'>
            View and control this user&apos;s enrolled MFA methods. Turning off a method disables it immediately. If this
            results in zero active methods, the user&apos;s active session(s) are logged out immediately and they will be
            required to complete MFA setup at their next login.
          </p>
          <div className='alert alert-info'>
            This user currently has <strong>{activeCount} of 3</strong> methods active.
          </div>
        </>
      )}
      {mode === 'self' && (
        <p className='text-muted'>
          Add an extra layer of security to your account. Choose one or more verification methods below. You can never be
          left with zero active methods — set up a new one before removing your last remaining method.
        </p>
      )}

      {methods.map(renderRow)}

      <Modal show={showMethodPicker} onHide={() => setShowMethodPicker(false)} centered size='lg'>
        <Modal.Body className='p-8'>
          <MfaMethodPicker
            methods={setupCandidates}
            selectedMethod={pickerSelection}
            onSelect={setPickerSelection}
            onContinue={async () => {
              if (pickerSelection == null) return
              setShowMethodPicker(false)
              await handleSetup(pickerSelection)
            }}
            loading={loading}
          />
        </Modal.Body>
      </Modal>

      <Modal show={setupMethod != null && setupMethod !== 2} onHide={() => setSetupMethod(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{setupMethod === 1 ? 'Set up email codes' : 'Set up text message codes'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            className='form-control mb-4'
            placeholder={setupMethod === 1 ? 'Email address' : 'Phone number'}
            value={contact}
            onChange={(e) => setContact(e.target.value)}
          />
          {mfaSession && (
            <>
              <MfaCodeInput value={code} onChange={setCode} />
              <button type='button' className='btn w-100 mt-4' style={{backgroundColor: '#D7383C', color: '#fff'}} onClick={verifySetup} disabled={loading}>
                Confirm & Finish Setup
              </button>
            </>
          )}
          {!mfaSession && (
            <button type='button' className='btn w-100' style={{backgroundColor: '#D7383C', color: '#fff'}} onClick={sendContactCode} disabled={loading}>
              Send Verification Code
            </button>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={setupMethod === 2} onHide={() => { setSetupMethod(null); setTotpScanned(false) }} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Scan with your authenticator app</Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center'>
          {qrUri && (
            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUri)}`} alt='QR' />
          )}
          <p className='text-muted mt-3 small'>Can&apos;t scan? Enter this key manually: {secretKey}</p>
          {!totpScanned && (
            <button type='button' className='btn mt-3' style={{backgroundColor: '#D7383C', color: '#fff'}} onClick={() => setTotpScanned(true)}>
              I&apos;ve Scanned the Code
            </button>
          )}
          {totpScanned && (
            <div className='mt-4'>
              <MfaCodeInput value={code} onChange={setCode} />
              <button type='button' className='btn w-100 mt-4' style={{backgroundColor: '#D7383C', color: '#fff'}} onClick={verifySetup} disabled={loading || code.length < 6}>
                Confirm & Finish Setup
              </button>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={disableTarget != null} onHide={() => setDisableTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Turn off this MFA Method?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mode === 'admin'
            ? 'This will remove the enrolled method immediately. If it is their last active method, they will be required to set up MFA again at their next login.'
            : 'Are you sure you want to disable this Multi-factor Authentication method?'}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={() => setDisableTarget(null)}>
            Cancel
          </button>
          <button type='button' className='btn btn-primary' onClick={confirmDisable} disabled={loading}>
            Yes
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
